import type { Vec2, Vec3 } from '../../geo/types';
import type { Frame, MeshBuilder, VertexStyle } from '../mesh/meshBuilder';
import { boxSolid, octahedronSolid, orientedFace } from './solids';
import { buildCoping } from './gableTrim';
import { plainGableOutline } from './gables';
import { emitFacade, emitWall, wallFace, type Body, type FacadeContext } from './houseFacades';
import { holeOf, meshGableWall, meshRectWall, type Pt, type WallMesh } from './wallMesh';
import { OPENING, openingsFor, type HouseOpenings, type Opening } from './openings';
import { lotFrame } from './lotFrame';
import type { DetailSink } from './detailSink';
import {
  houseFrame,
  houseStyles,
  type HouseGeometry,
  type HousePlan,
  type HouseStyles,
} from './houseParts';
import { buildHouseRoof } from './houseRoof';
import { buildStoepen, stoepWalls } from './stoep';
import { buildLuifel } from './luifel';
import { buildShopRoom, hasShopRoom, shopRoomOf } from './shopRoom';
import { anchorLevels, anchorTimberHouse } from './wallAnchors';
import { placeWares, wareStyles } from './shopWares';
import { damrakWaterOpenings, DAMRAK_WATER_RECESS, isDamrakWaterfront } from './damrakWaterfront';

/* Sources and measurements: docs/plans/houses.md, "Timber fronts". */
export const TIMBER = {
  /**
   * Each storey stands out over the one below, and the gable over the top storey, by 7 duim
   * (about 18 cm): the most the keur of 1531/32 allowed, where Zeedijk 1 has about 20 cm
   * (Meischke, Jaarboek Monumentenzorg 1996; Huizen in Nederland II, 1995).
   */
  jetty: 0.18,
  /** Barge boards (windveren) along the gable's edges, a plank on edge standing out of the gable's face. */
  bargeboard: { width: 0.25, lift: 0.04, proud: 0.04 },
  bargeboardBack: 0.06,
  /** The post (makelaar) where the barge boards meet: over the apex, hanging below it, with a pointed top. */
  finial: { half: 0.05, rise: 0.7, hang: 0.5, knob: 0.05 },
} as const;

/** Storey k's front stands at fronts[k], the ground floor's deepest in; the gable at fronts[storeys] = 0. */
interface Storeys {
  readonly count: number;
  readonly height: number;
  readonly fronts: readonly number[];
}

function storeysOf(count: number, height: number): Storeys {
  return {
    count,
    height,
    fronts: Array.from({ length: count + 1 }, (_, k) => (count - k) * TIMBER.jetty),
  };
}

/** Where the front stands at height `y`: the front of the storey that height falls in. */
function frontAt(s: Storeys, y: number): number {
  return s.fronts[Math.min(s.count - 1, Math.max(0, Math.floor(y / s.height)))]!;
}

function shifted(mesh: WallMesh, du: number, dv: number): WallMesh {
  return {
    wall: mesh.wall.map((fan) => fan.map((p): Pt => [p[0] + du, p[1] + dv])),
    panes: mesh.panes.map((p) => ({
      ...p,
      x0: p.x0 + du,
      x1: p.x1 + du,
      y0: p.y0 + dv,
      y1: p.y1 + dv,
    })),
  };
}

/** One storey's stretch of a wall, from `from` to `to` along it; the openings in the wall's own coordinates. */
function emitStoreyWall(
  ctx: FacadeContext,
  face: ReturnType<typeof wallFace>,
  span: {
    readonly from: number;
    readonly to: number;
    readonly foot: number;
    readonly height: number;
  },
  openings: readonly Opening[]
): void {
  const holes = openings.map((o) => holeOf({ ...o, x: o.x - span.from, y: o.y - span.foot }));
  const mesh = meshRectWall(span.to - span.from, span.height, holes);
  emitWall(ctx, face, shifted(mesh, span.from, span.foot), openings);
}

function inStorey(openings: readonly Opening[], k: number, height: number): Opening[] {
  return openings.filter((o) => Math.floor((o.y + 1e-6) / height) === k);
}

/* A jetty's underside at each storey's floor, and under the gable at the eaves. */
function emitSoffits(
  builder: MeshBuilder,
  style: VertexStyle,
  frame: Frame,
  s: Storeys,
  half: number
): void {
  for (let k = 1; k <= s.count; k++) {
    const [x0, x1, y] = [s.fronts[k]!, s.fronts[k - 1]!, k * s.height];
    const pts: Vec3[] = [
      [x0, y, -half],
      [x1, y, -half],
      [x1, y, half],
      [x0, y, half],
    ];
    orientedFace(builder, style, frame, pts, [0, -1, 0], (p): Vec2 => [p[2] + half, p[0]]);
  }
}

/* The storeys' fronts, each over the one below, and the gable over the top one on the lot's front line. */
function emitFronts(ctx: FacadeContext, s: Storeys, body: Body, openings: HouseOpenings): void {
  const width = body.half * 2;
  for (let k = 0; k < s.count; k++) {
    const face = wallFace([s.fronts[k]!, 0, -body.half], [-1, 0, 0]);
    const span = { from: 0, to: width, foot: k * s.height, height: s.height };
    emitStoreyWall(ctx, face, span, inStorey(openings.front, k, s.height));
  }
  const gable = meshGableWall(width, body.eaves, body.ridge, openings.frontGable.map(holeOf));
  emitWall(ctx, wallFace([0, 0, -body.half], [-1, 0, 0]), gable, openings.frontGable);
}

/**
 * The brick side walls, storey by storey behind each storey's front. The left wall runs from the
 * back corner and the right wall from the gable's line, so a side window lands where the layout put it.
 */
function emitSides(ctx: FacadeContext, s: Storeys, body: Body, openings: HouseOpenings): void {
  const { depth, half } = body;
  const margin = OPENING.sideMargin;
  const left = wallFace([depth, 0, -half], [0, 0, -1]);
  const right = wallFace([0, 0, half], [0, 0, 1]);
  for (let k = 0; k < s.count; k++) {
    const front = s.fronts[k]!;
    const foot = k * s.height;
    const leftOpenings = inStorey(openings.left, k, s.height).filter(
      (o) => o.x + o.width <= depth - front - margin
    );
    const rightOpenings = inStorey(openings.right, k, s.height).filter(
      (o) => o.x >= front + margin
    );
    emitStoreyWall(ctx, left, { from: 0, to: depth - front, foot, height: s.height }, leftOpenings);
    emitStoreyWall(ctx, right, { from: front, to: depth, foot, height: s.height }, rightOpenings);
  }
}

function buildMakelaar(
  builder: MeshBuilder,
  style: VertexStyle,
  frame: Frame,
  ridge: number
): void {
  const { half, rise, hang, knob } = TIMBER.finial;
  const [front, back] = [-TIMBER.bargeboard.proud, TIMBER.bargeboardBack];
  boxSolid(builder, style, frame, [front, ridge - hang, -half], [back, ridge + rise, half], {
    top: true,
    bottom: true,
  });
  octahedronSolid(builder, style, frame, [(front + back) / 2, ridge + rise + knob / 2, 0], knob);
}

function buildTrim(
  builder: MeshBuilder,
  styles: HouseStyles,
  frame: Frame,
  body: Body,
  pitch: number
): void {
  const gable = plainGableOutline(body.half * 2, body.eaves, pitch);
  const bounds = { xBack: TIMBER.bargeboardBack, halfWidth: body.half };
  buildCoping(builder, styles.trim, frame, gable.coping, bounds, TIMBER.bargeboard);
  buildMakelaar(builder, styles.trim, frame, body.ridge);
}

/**
 * A house with a timber front (docs/plans/houses.md, "Timber fronts"): storeys jettied one over
 * the other toward the street, the pointed gable over the top storey with barge boards and a
 * makelaar, brick side walls, and a timber back. Its openings are laid out as every front's, from
 * the painting's window rows (STYLE §8.1).
 */
export function buildTimberHouse(
  builder: MeshBuilder,
  plan: HousePlan,
  details: DetailSink
): HouseGeometry {
  const { lot, params } = plan;
  const { width, depth } = lotFrame(lot);
  const s = storeysOf(params.storeys, params.storeyHeight);
  const eaves = s.count * s.height;
  const body: Body = {
    x0: 0,
    depth,
    half: width / 2,
    eaves,
    ridge: eaves + (width / 2) * Math.tan(params.pitch),
  };
  const frame = houseFrame(lot);
  const styles = houseStyles(plan, width, depth, eaves);
  const spec = { ...params, width, length: depth, party: plan.party, shop: plan.shop };
  const ordinary = openingsFor(spec, plan.rng.fork('openings'));
  const waterfront = isDamrakWaterfront(lot);
  const openings = waterfront ? damrakWaterOpenings(spec, ordinary) : ordinary;
  const ctx: FacadeContext = {
    builder,
    styles,
    frame,
    details,
    edgeId: plan.edgeId,
    openFrontDoor: plan.openFrontDoor,
  };

  const before = builder.triangleCount;
  emitFronts(ctx, s, body, openings);
  emitSoffits(builder, styles.wall, frame, s, body.half);
  emitSides({ ...ctx, styles: { ...styles, wall: styles.side } }, s, body, openings);
  anchorTimberHouse(
    ctx,
    body,
    anchorLevels(s.count, s.height),
    s.fronts[0]!,
    openings,
    plan.streets
  );
  placeWares(
    { ...ctx, styles: wareStyles(styles) },
    wallFace([s.fronts[0]!, 0, -body.half], [-1, 0, 0]),
    inStorey(openings.front, 0, s.height),
    plan.wares
  );
  emitFacade(
    ctx,
    wallFace([depth, 0, body.half], [1, 0, 0]),
    { width, eaves, ridge: body.ridge },
    { wall: openings.back, gable: openings.backGable },
    waterfront ? DAMRAK_WATER_RECESS : 0
  );
  buildHouseRoof(builder, styles, frame, body, params, details);
  buildStoepen(
    builder,
    styles.stone,
    frame,
    plan.stoep,
    stoepWalls(s.fronts[0]!, body.half, depth, plan.party)
  );
  if (plan.shop.luifel)
    buildLuifel(builder, styles.luifel, frame, {
      frontAt: (y) => frontAt(s, y),
      half: body.half,
      eaves,
    });
  if (hasShopRoom(plan.shop))
    buildShopRoom(
      builder,
      styles.room,
      frame,
      shopRoomOf(body, s.fronts[0]!, params),
      openings,
      body
    );
  buildTrim(builder, styles, frame, body, params.pitch);
  return {
    eavesHeight: eaves,
    ridgeHeight: body.ridge,
    triangleCount: builder.triangleCount - before,
  };
}
